import type { BankEntry, Locale, PersonaEvent } from './banks.js';
import { BANKS } from './banks.js';
import { personaById } from './cast.js';
import type { StyleId } from './style.js';

/**
 * Line picking with the two guardrails from the spec: an over-use limiter (the chatter setting
 * Off / A little / A lot -> per-game maxima) and an anti-repeat memory (the last N lines per
 * persona × event never repeat while alternatives exist).
 */

export type ChatterLevel = 'off' | 'little' | 'lot';

const MAX_LINES_PER_GAME: Record<Exclude<ChatterLevel, 'off'>, number> = { little: 3, lot: 8 };
const ANTI_REPEAT_WINDOW = 3;

export interface ChatterContext {
  readonly personaId: string;
  readonly event: PersonaEvent;
  readonly locale: Locale;
  /** Monotonic counter: how many lines this persona has spoken this game. */
  readonly spoken: number;
  readonly chatter: ChatterLevel;
  /** Recent lines (most recent first) — the anti-repeat window. */
  readonly recent: readonly string[];
}

/** The authored text of an entry, whatever shape it is. */
export function lineText(entry: BankEntry): string {
  return typeof entry === 'string' ? entry : entry.text;
}

function taggedWith(entry: BankEntry, styleId: StyleId | undefined): boolean {
  return typeof entry !== 'string' && styleId !== undefined && entry.styles.includes(styleId);
}

function untagged(entry: BankEntry): boolean {
  return typeof entry === 'string';
}

/**
 * The style-aware pool (P5 "talk and moves agree"), in order of preference:
 *
 * 1. fresh lines TAGGED with the speaker's own style, if any;
 * 2. else any fresh acceptable line = untagged OR own-style-tagged — a line
 *    tagged for a style the speaker does not have is EXCLUDED, so "a persona
 *    whose card says aggressive does not play cautious lines" holds by
 *    construction (banks are persona-keyed, so a speaker only ever draws from
 *    its own bank);
 * 3. else the acceptable set ignoring recency (anti-repeat may exhaust a small
 *    bank);
 * 4. defensive last resort: the full bank — unreachable while the bank test
 *    guarantees >=1 acceptable line per persona × event × locale.
 *
 * Exported so the mismatch-exclusion rule can be tested against synthetic
 * entries (a persona's own bank deliberately contains no cross-style tags —
 * those would be dead content for their owner).
 */
export function selectPool(
  entries: readonly BankEntry[],
  styleId: StyleId | undefined,
  recent: readonly string[],
): BankEntry[] {
  const recentSet = new Set(recent.slice(0, ANTI_REPEAT_WINDOW));
  const acceptable = entries.filter((e) => untagged(e) || taggedWith(e, styleId));
  const fresh = acceptable.filter((e) => !recentSet.has(lineText(e)));
  const taggedFresh = fresh.filter((e) => taggedWith(e, styleId));
  if (taggedFresh.length > 0) return taggedFresh;
  if (fresh.length > 0) return fresh;
  if (acceptable.length > 0) return acceptable;
  return [...entries];
}

/** Deterministic line choice; `random` is injected so tests and replays are stable. */
export function pickLine(ctx: ChatterContext, random: () => number = Math.random): string | null {
  if (ctx.chatter === 'off') return null;
  if (ctx.spoken >= MAX_LINES_PER_GAME[ctx.chatter]) return null;
  const persona = BANKS[ctx.personaId];
  if (!persona) return null;
  const bank = persona[ctx.event]?.[ctx.locale] ?? persona[ctx.event]?.en ?? [];
  if (bank.length === 0) return null;
  const styleId = personaById(ctx.personaId)?.style;
  const pool = selectPool(bank, styleId, ctx.recent);
  const pick = pool[Math.floor(random() * pool.length)];
  return pick ? lineText(pick) : null;
}

/** A persona's picker blurb, in the requested locale (EN as the fallback). */
export function blurbFor(personaId: string, locale: Locale): string | null {
  const p = personaById(personaId);
  if (!p) return null;
  return p.blurb[locale] ?? p.blurb.en;
}
